import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LESSON_TYPES } from 'constants/common';
import { DEFAULT_LESSON_LOCALE, LESSON_LOCALES } from 'constants/lessonLocale';

import * as api from 'api/methods';

import {
  extractLessonLocalizationPayload,
  getChangedLocalizationProperties,
  getLocalizationPayloadByKeys,
  getLocalizationPayloadForLocale,
  areTasksFilled,
  isLocalizationCompleteForLocale,
  LESSON_LOCALIZATION_FIELDS,
  mergeDifferenceLessonAssets,
} from 'helpers/lessonLocalization.helpers';

import { useFetch } from 'hooks/useFetch';

const fetchMergedLessonByType = ({
  lessonId,
  lessonType,
  locale,
}: {
  lessonId: string;
  lessonType: string;
  locale: string;
}) => {
  switch (lessonType) {
    case LESSON_TYPES.THEORY_LESSON: {
      return api.getTheoryLessonById({ lessonId, locale });
    }
    case LESSON_TYPES.QUIZ: {
      return api.getQuizById({ lessonId, locale });
    }
    case LESSON_TYPES.FIGMA_LESSON: {
      return api.getFigmaLessonById({ lessonId, locale });
    }
    case LESSON_TYPES.CONNECTION_QUIZ: {
      return api.getConnectionQuizById({ lessonId, locale });
    }
    case LESSON_TYPES.TWO_COLUMNS_LESSON: {
      return api.getTwoColumnsLessonById({ lessonId, locale });
    }
    case LESSON_TYPES.DIFFERENCE_LESSON: {
      return api.getDifferenceLessonById({ lessonId, locale });
    }
    case LESSON_TYPES.COLOR_MATCH_LESSON: {
      return api.getColorMatchLessonById({ lessonId, locale });
    }
    default: {
      return Promise.resolve(null);
    }
  }
};

const shouldPreserveDifferenceAssets = (
  lessonType: string,
  fields?: string[],
) => {
  const generatedFields =
    fields ?? LESSON_LOCALIZATION_FIELDS[lessonType] ?? [];

  return (
    lessonType === LESSON_TYPES.DIFFERENCE_LESSON &&
    (generatedFields.includes('leftItem') ||
      generatedFields.includes('rightItem'))
  );
};

const preserveDifferenceLessonAssets = async ({
  lessonId,
  lessonType,
  locale,
  sourceLocale,
}: {
  lessonId: string;
  lessonType: string;
  locale: string;
  sourceLocale: string;
}) => {
  const [sourceLesson, targetLesson] = await Promise.all([
    fetchMergedLessonByType({
      lessonId,
      lessonType,
      locale: sourceLocale,
    }),
    fetchMergedLessonByType({
      lessonId,
      lessonType,
      locale,
    }),
  ]);

  await api.upsertLessonLocalization({
    lessonId,
    locale,
    lessonType,
    payload: extractLessonLocalizationPayload(
      lessonType,
      mergeDifferenceLessonAssets(sourceLesson, targetLesson) ?? {},
    ),
  });
};

const haveDifferenceItemAssetsChanged = (
  currentLesson: Record<string, any> | null | undefined,
  nextLesson: Record<string, any> | null | undefined,
) =>
  currentLesson?.leftItem?.url !== nextLesson?.leftItem?.url ||
  currentLesson?.rightItem?.url !== nextLesson?.rightItem?.url ||
  currentLesson?.leftItem?.correct !== nextLesson?.leftItem?.correct ||
  currentLesson?.rightItem?.correct !== nextLesson?.rightItem?.correct;

export const useLessonLocale = ({
  lessonId,
  lessonType,
  baseLesson,
  availableLocales = LESSON_LOCALES,
}: {
  lessonId: string;
  lessonType: string;
  baseLesson: any;
  availableLocales?: readonly string[];
}) => {
  const [activeLocale, setActiveLocale] = useState(DEFAULT_LESSON_LOCALE);
  const [mergedLesson, setMergedLesson] = useState<any>(null);
  const [mergedLessonLoading, setMergedLessonLoading] = useState(false);
  const [referenceLesson, setReferenceLesson] = useState<any>(null);
  const [localeCompletionByLocale, setLocaleCompletionByLocale] = useState<
    Record<string, boolean>
  >({});

  const baseLessonRef = useRef(baseLesson);
  baseLessonRef.current = baseLesson;

  const localizationsRef = useRef<any[]>([]);

  const activeLocaleRef = useRef(activeLocale);
  activeLocaleRef.current = activeLocale;

  const lessonsByLocaleRef = useRef<Record<string, any>>({});
  const formTasksByLocaleRef = useRef<Record<string, unknown>>({});
  const syncRequestIdRef = useRef(0);
  const skipActiveLocaleLoadRef = useRef(true);
  const hasInitialSyncedRef = useRef(false);
  const hasPreservedDifferenceAssetsRef = useRef(false);

  const {
    data: localizations,
    loading: localizationsLoading,
    setState: setLocalizationsState,
  } = useFetch<any[]>(
    {
      fetcher: () => api.getLessonLocalizations(lessonId),
      defaultData: [],
      immediately: Boolean(lessonId),
      stopRequest: !lessonId,
    },
    lessonId,
    [lessonId],
  );

  localizationsRef.current = localizations ?? [];

  const computeLocaleCompletionByLocale = useCallback(
    (localizationsList: any[]) => {
      if (!baseLessonRef.current) {
        return {};
      }

      return Object.fromEntries(
        availableLocales.map((locale) => {
          const formTasks = formTasksByLocaleRef.current[locale];
          const cachedMergedLesson = lessonsByLocaleRef.current[locale];
          const mergedLesson =
            formTasks != null
              ? {
                  ...(cachedMergedLesson ?? {}),
                  tasks: formTasks,
                }
              : cachedMergedLesson;

          return [
            locale,
            isLocalizationCompleteForLocale({
              lessonType,
              locale,
              baseLesson: baseLessonRef.current,
              localizations: localizationsList,
              mergedLesson,
            }),
          ];
        }),
      );
    },
    [availableLocales, lessonType],
  );

  const localesWithContent = useMemo(() => {
    const completedLocales = availableLocales.filter(
      (locale) => localeCompletionByLocale[locale],
    );

    if (!mergedLesson || mergedLessonLoading) {
      return completedLocales;
    }

    const isActiveLocaleComplete = Boolean(
      localeCompletionByLocale[activeLocale],
    );

    if (isActiveLocaleComplete) {
      return completedLocales.includes(activeLocale)
        ? completedLocales
        : [...completedLocales, activeLocale];
    }

    return completedLocales.filter((locale) => locale !== activeLocale);
  }, [
    activeLocale,
    availableLocales,
    localeCompletionByLocale,
    mergedLesson,
    mergedLessonLoading,
  ]);

  const syncLocales = useCallback(
    async ({
      localesToFetch = availableLocales,
      applyActiveLocale,
    }: {
      localesToFetch?: readonly string[];
      applyActiveLocale?: string;
    } = {}) => {
      if (!lessonId || !baseLessonRef.current) {
        setLocaleCompletionByLocale({});
        setReferenceLesson(null);
        return;
      }

      const requestId = ++syncRequestIdRef.current;
      setMergedLessonLoading(true);

      try {
        const uniqueLocales = [...new Set(localesToFetch)];
        const lessonEntries = await Promise.all(
          uniqueLocales.map(async (locale) => {
            const lesson = await fetchMergedLessonByType({
              lessonId,
              lessonType,
              locale,
            });

            return [locale, lesson] as const;
          }),
        );

        if (requestId !== syncRequestIdRef.current) {
          return;
        }

        const fetchedLessonsByLocale = Object.fromEntries(lessonEntries);

        lessonsByLocaleRef.current = {
          ...lessonsByLocaleRef.current,
          ...fetchedLessonsByLocale,
        };

        const ruLesson =
          lessonsByLocaleRef.current[DEFAULT_LESSON_LOCALE] ?? null;

        setReferenceLesson(ruLesson);

        const nextCompletion = computeLocaleCompletionByLocale(
          localizationsRef.current,
        );

        setLocaleCompletionByLocale(nextCompletion);

        const localeToApply =
          applyActiveLocale ?? activeLocaleRef.current;
        const mergedLessonForActiveLocale =
          lessonsByLocaleRef.current[localeToApply];

        if (mergedLessonForActiveLocale) {
          setMergedLesson(mergedLessonForActiveLocale);
        }
      } finally {
        if (requestId === syncRequestIdRef.current) {
          setMergedLessonLoading(false);
        }
      }
    },
    [availableLocales, computeLocaleCompletionByLocale, lessonId, lessonType],
  );

  useEffect(() => {
    skipActiveLocaleLoadRef.current = true;
    lessonsByLocaleRef.current = {};
    formTasksByLocaleRef.current = {};
    syncRequestIdRef.current += 1;
    hasInitialSyncedRef.current = false;
  }, [lessonId]);

  useEffect(() => {
    if (
      !lessonId ||
      localizationsLoading ||
      !baseLesson?.id ||
      hasInitialSyncedRef.current
    ) {
      return;
    }

    hasInitialSyncedRef.current = true;
    void syncLocales({ applyActiveLocale: activeLocaleRef.current });
  }, [baseLesson?.id, lessonId, localizationsLoading, syncLocales]);

  useEffect(() => {
    if (!lessonId || localizationsLoading || !baseLesson?.id) {
      return;
    }

    const nextCompletion = computeLocaleCompletionByLocale(localizations ?? []);

    setLocaleCompletionByLocale(nextCompletion);
  }, [
    baseLesson?.id,
    computeLocaleCompletionByLocale,
    lessonId,
    localizations,
    localizationsLoading,
  ]);

  const isDefaultLocale = activeLocale === DEFAULT_LESSON_LOCALE;
  const localeContentLoading = mergedLessonLoading || mergedLesson == null;
  const editorLesson = mergedLesson;

  const refreshLocalizations = useCallback(() => {
    if (!lessonId) {
      return Promise.resolve([]);
    }

    return api.getLessonLocalizations(lessonId).then((response) => {
      localizationsRef.current = response;

      setLocalizationsState({
        loading: false,
        error: null,
        data: response,
      });

      return response;
    });
  }, [lessonId, setLocalizationsState]);

  const loadMergedLesson = useCallback(
    async (locale: string) => {
      setMergedLessonLoading(true);

      try {
        const lesson = await fetchMergedLessonByType({
          lessonId,
          lessonType,
          locale,
        });

        lessonsByLocaleRef.current[locale] = lesson;
        setMergedLesson(lesson);
        setLocaleCompletionByLocale(
          computeLocaleCompletionByLocale(localizationsRef.current),
        );

        return lesson;
      } finally {
        setMergedLessonLoading(false);
      }
    },
    [lessonId, lessonType, computeLocaleCompletionByLocale],
  );

  useEffect(() => {
    if (!lessonId) {
      return;
    }

    if (skipActiveLocaleLoadRef.current) {
      skipActiveLocaleLoadRef.current = false;
      return;
    }

    void loadMergedLesson(activeLocale);
  }, [activeLocale, lessonId, loadMergedLesson]);

  const handleLocaleChange = useCallback((locale: string) => {
    setActiveLocale(locale);
  }, []);

  const updateLocaleTabCompletionFromFormTasks = useCallback(
    (locale: string, tasks: unknown) => {
      if (!Array.isArray(tasks)) {
        return;
      }

      const existingTasks = formTasksByLocaleRef.current[locale];
      const incomingTasksFilled = areTasksFilled(tasks);
      const existingTasksFilled = areTasksFilled(existingTasks);

      if (!incomingTasksFilled && existingTasksFilled) {
        setLocaleCompletionByLocale(
          computeLocaleCompletionByLocale(localizationsRef.current),
        );
        return;
      }

      if (incomingTasksFilled || existingTasks == null) {
        formTasksByLocaleRef.current[locale] = tasks;
      }

      setLocaleCompletionByLocale(
        computeLocaleCompletionByLocale(localizationsRef.current),
      );
    },
    [computeLocaleCompletionByLocale],
  );

  const updateLocaleCompletion = useCallback(() => {
    setLocaleCompletionByLocale(
      computeLocaleCompletionByLocale(localizationsRef.current),
    );
  }, [computeLocaleCompletionByLocale]);

  const upsertLocalization = useCallback(
    async (
      payload: Record<string, any>,
      options?: { reloadMergedLesson?: boolean },
    ) => {
      await api.upsertLessonLocalization({
        lessonId,
        locale: activeLocale,
        lessonType,
        payload,
      });

      const shouldReloadMergedLesson = options?.reloadMergedLesson ?? true;

      if (shouldReloadMergedLesson) {
        await loadMergedLesson(activeLocale);
      } else {
        const lesson = await fetchMergedLessonByType({
          lessonId,
          lessonType,
          locale: activeLocale,
        });

        lessonsByLocaleRef.current[activeLocale] = lesson;
      }

      await refreshLocalizations();
      updateLocaleCompletion();
    },
    [
      activeLocale,
      lessonId,
      lessonType,
      loadMergedLesson,
      refreshLocalizations,
      updateLocaleCompletion,
    ],
  );

  useEffect(() => {
    hasPreservedDifferenceAssetsRef.current = false;
  }, [activeLocale, lessonId]);

  useEffect(() => {
    if (
      hasPreservedDifferenceAssetsRef.current ||
      lessonType !== LESSON_TYPES.DIFFERENCE_LESSON ||
      activeLocale === DEFAULT_LESSON_LOCALE ||
      localeContentLoading ||
      !editorLesson ||
      !referenceLesson
    ) {
      return;
    }

    const lessonWithSourceAssets = mergeDifferenceLessonAssets(
      referenceLesson,
      editorLesson,
    );

    if (
      !haveDifferenceItemAssetsChanged(editorLesson, lessonWithSourceAssets)
    ) {
      return;
    }

    hasPreservedDifferenceAssetsRef.current = true;

    void upsertLocalization(
      extractLessonLocalizationPayload(
        lessonType,
        lessonWithSourceAssets ?? {},
      ),
    );
  }, [
    activeLocale,
    editorLesson,
    lessonType,
    localeContentLoading,
    referenceLesson,
    upsertLocalization,
  ]);

  const saveLocalizationChanges = useCallback(
    async (
      data: Record<string, any>,
      options?: { reloadMergedLesson?: boolean },
    ) => {
      if (localeContentLoading || !editorLesson) {
        return false;
      }

      const payload = extractLessonLocalizationPayload(lessonType, data);
      let changedPropertyKeys = getChangedLocalizationProperties({
        lessonType,
        currentLesson: editorLesson,
        nextPayload: payload,
      });

      if (
        changedPropertyKeys.includes('tasks') &&
        !areTasksFilled(payload.tasks)
      ) {
        const existingTasksFilled =
          areTasksFilled(editorLesson?.tasks) ||
          areTasksFilled(
            getLocalizationPayloadForLocale(
              localizationsRef.current,
              activeLocale,
            ).tasks,
          );

        if (existingTasksFilled) {
          changedPropertyKeys = changedPropertyKeys.filter(
            (key) => key !== 'tasks',
          );
        }
      }

      if (!changedPropertyKeys.length) {
        return false;
      }

      await upsertLocalization(
        getLocalizationPayloadByKeys({
          payload,
          keys: changedPropertyKeys,
        }),
        options,
      );

      return true;
    },
    [activeLocale, editorLesson, lessonType, localeContentLoading, upsertLocalization],
  );

  const generateFromLocale = useCallback(
    async ({
      sourceLocale,
      fields,
    }: {
      sourceLocale: string;
      fields?: string[];
    }) => {
      const result = await api.generateLessonLocalizationFrom({
        lessonId,
        locale: activeLocale,
        sourceLocale,
        lessonType,
        fields,
      });

      if (shouldPreserveDifferenceAssets(lessonType, fields)) {
        await preserveDifferenceLessonAssets({
          lessonId,
          lessonType,
          locale: activeLocale,
          sourceLocale,
        });
      }

      await refreshLocalizations();
      await syncLocales({ applyActiveLocale: activeLocale });

      return result;
    },
    [
      activeLocale,
      lessonId,
      lessonType,
      refreshLocalizations,
      syncLocales,
    ],
  );

  return {
    activeLocale,
    availableLocales,
    baseLesson,
    editorLesson,
    generateFromLocale,
    referenceLesson,
    isDefaultLocale,
    loadMergedLesson,
    localeContentLoading,
    localesWithContent,
    localizations,
    localizationsLoading,
    mergedLessonLoading,
    onLocaleChange: handleLocaleChange,
    refreshLocalizations,
    saveLocalizationChanges,
    syncLocales,
    updateLocaleTabCompletionFromFormTasks,
    upsertLocalization,
  };
};
